import { useEffect, useState } from "react";
import "./events.scss";
import { getEvents } from "../../components/api/api";
import EventRegistrationModal from "../../components/EventRegistrationModal/EventRegistrationModal";
import Button from "components/Button/Button";
import Loader from "../../components/Loader";
import eventsHero from "../../assets/images/events.png";
import eventsFallbackImage from "../../assets/images/events.png";

const getEventImageUrl = (url) => {
  if (!url || typeof url !== "string") return url;
  const fileIdMatch =
    url.match(/\/file\/d\/([^/]+)/) || url.match(/[?&]id=([^&]+)/);
  if (fileIdMatch) {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    return `${backend}/api/image-proxy?id=${encodeURIComponent(fileIdMatch[1])}`;
  }
  return url;
};

const formatDateWithWeekday = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return new Intl.DateTimeFormat("en-CA", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(d);
};

const EventCard = ({ event, onReserve }) => {
  const [expanded, setExpanded] = useState(false);
  const eventName = event.NAME || event.name;
  const eventImage = getEventImageUrl(
    event?.["IMAGE URL"] ||
      event?.["Image URL"] ||
      event?.imageUrl ||
      eventsFallbackImage,
  );
  const description = event.DESCRIPTION || event.description;
  const date = event.DATE || event.date;
  const time = event.TIME || event.time;
  const location = event.LOCATION || event.location;
  const reservationCostRaw =
    event?.["RESERVATION COST"] || event?.reservationCost || 0;
  const reservationCost =
    typeof reservationCostRaw === "string"
      ? parseFloat(reservationCostRaw.replace(/[^0-9.]/g, ""))
      : parseFloat(reservationCostRaw) || 0;
  const formattedDate = formatDateWithWeekday(date) || date;

  const descriptionParagraphs = description
    ? description
        .split(/\n\n+/)
        .map((para) => para.trim())
        .filter(Boolean)
    : [];

  return (
    <div className={`event-list-item ${expanded ? "expanded" : ""}`}>
      <div className="event-list-item-content">
        <div className="event-list-item-header">
          <h2 className="event-list-item-title">{eventName}</h2>
          <div className="event-list-item-meta">
            {date && (
              <span className="event-list-item-date">
                {formattedDate}
                {time ? `, ${time}` : ""}
              </span>
            )}
            {location && (
              <span className="event-list-item-location">{location}</span>
            )}
            <span className="event-list-item-cost">
              {reservationCost > 0
                ? `$${reservationCost.toFixed(2)} Deposit`
                : "FREE"}
            </span>
          </div>
        </div>
        <div className="event-list-item-body">
          <div className="event-list-item-image-wrap">
            <img
              src={eventImage}
              alt={eventName}
              className="event-list-item-image"
              referrerPolicy="no-referrer"
            />
          </div>
          <div className="event-list-item-body-content">
            {descriptionParagraphs.length > 0 && (
              <div
                className={`event-list-item-description ${expanded ? "expanded" : "collapsed"}`}
              >
                {expanded ? (
                  descriptionParagraphs.map((para, i) => (
                    <p
                      key={i}
                      className="event-list-item-description-para"
                      style={{ whiteSpace: "pre-line" }}
                    >
                      {para}
                    </p>
                  ))
                ) : (
                  <p
                    className="event-list-item-description-para"
                    style={{ whiteSpace: "pre-line" }}
                  >
                    {descriptionParagraphs.join("\n\n")}
                  </p>
                )}
              </div>
            )}
            <div className="event-list-item-actions">
              {descriptionParagraphs.length > 0 && (
                <button
                  type="button"
                  className="event-list-item-read-more"
                  onClick={() => setExpanded(!expanded)}
                >
                  {expanded ? "Read less" : "Read more"}
                </button>
              )}
              <Button
                variant="secondary"
                className="event-list-item-reserve"
                disabled={false}
                onClick={() => onReserve(event)}
              >
                Reserve spot
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const fetchEvents = async () => {
      const data = await getEvents();
      setEvents(Array.isArray(data) ? data : []);
      setLoading(false);
    };
    fetchEvents();
  }, []);

  const handleReserve = (event) => {
    setSelectedEvent(event);
    setModalOpen(true);
  };

  const handleRegistrationSuccess = () => {
    setModalOpen(false);
    setSelectedEvent(null);
  };

  if (loading) {
    return (
      <div className="events">
        <Loader hasHeader={true} />
      </div>
    );
  }

  return (
    <div className="events">
      <div className="content-container">
        <div className="events-hero">
          <div className="events-hero-title-container">
            <img
              src={eventsHero}
              alt="Events Hero"
              className="events-hero-image"
            />
            <h1 className="events-title">Upcoming Events</h1>
          </div>
          <p className="events-description">
            We host a variety of community events! There’s always something fun
            happening and everyone is welcome!
          </p>

          <p className="events-description">
            We strive to keep things accessible, with most events offered on a
            sliding scale so everyone can join in.
          </p>
        </div>
        {events.length === 0 ? (
          <p className="events-empty">
            No events at the moment. Check back soon!
          </p>
        ) : (
          <div className="events-list">
            {events.map((event) => (
              <EventCard
                key={event.NAME || event.name || Math.random()}
                event={event}
                onReserve={handleReserve}
              />
            ))}
          </div>
        )}
      </div>
      <EventRegistrationModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        event={selectedEvent}
        onSuccess={handleRegistrationSuccess}
      />
    </div>
  );
}

export default Events;
